export type LanguageCode = 'tr' | 'en';

const getLanguageFromSearch = (): LanguageCode | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const language = new URLSearchParams(window.location.search).get('language')?.toLowerCase();
    return language === 'en' ? 'en' : language === 'tr' ? 'tr' : null;
};

export const getCurrentLanguage = (): LanguageCode => {
    if (typeof window === 'undefined') {
        return 'tr';
    }

    const searchLanguage = getLanguageFromSearch();
    if (searchLanguage) {
        return searchLanguage;
    }

    const sessionLanguage = sessionStorage.getItem('language')?.toLowerCase();

    if (sessionLanguage === 'en') {
        return 'en';
    }

    if (sessionLanguage === 'tr') {
        return 'tr';
    }

    return 'tr';
};

export const getSpeechLocale = (): string => {
    return getCurrentLanguage() === 'en' ? 'en-US' : 'tr-TR';
};

export const getDateLocale = (): string => {
    return getCurrentLanguage() === 'en' ? 'en-US' : 'tr-TR';
};

const translations = {
    tr: {
        ok: 'Tamam',
        amazing: 'Harika!',
        yes: 'Evet',
        no: 'Hayir',
        info: 'Bilgilendirme',
        error: 'Hata',
        close: 'Kapat',
        approve: 'Onayla',
        send: 'Gonder',
        uploading: 'Yukleniyor...',
        agreementTitle: 'Sozlesme Onayi',
        agreementDescription: 'Lutfen asagidaki sozlesmeyi okuyup onaylayiniz. Devam etmek icin sozlesmeyi kabul etmeniz gerekmektedir.',
        serviceAgreement: 'Hizmet Sozlesmesi',
        consentText: 'Kisisel Verilerin Islenmesine Iliskin Acik Riza Metni',
        uploadTitle: 'Resim veya Dosyanizi Ekleyin',
        uploadDescription: 'HappAI ile belgelerini, raporlarini yorumlayabilmesi icin lutfen HappAI dosya yukleme alanina surukle veya dosyani sec.',
        uploadDropLabel: 'Surukle ve Birak veya Dosya Sec',
        uploadError: 'Dosya yukleme sirasinda bir hata olustu.',
        analyzingDocuments: 'Belgeleriniz inceleniyor',
        uploadSuccess: (fileName: string) => `Dosya basariyla yuklendi: ${fileName}`,
        uploadSuccessReply: 'HappAI belgen yuklendi. Bana bilmek istediklerini sorabilirsin.',
        permissionDeniedText: 'Mikrofon izni verilmedigi icin sesli mesaj ozelligi kullanilamiyor. Izin vermek icin tekrar deneyebilirsiniz.',
        selectCategoryFirst: 'Lutfen once bir kategori seciniz.',
        speechNotSupported: 'Tarayiciniz ses tanima ozelligini desteklemiyor.',
        microphoneAccessError: 'Mikrofon erisim hatasi. Lutfen tarayicinizin veya cihazin ses tanima ozelliklerini kontrol edin.',
        stopListening: 'Dinlemeyi Durdur',
        voiceMessage: 'Sesli Mesaj',
        voiceChat: 'Sesli Sohbet',
        voiceChatListening: 'Sizi dinliyorum...',
        voiceChatThinking: 'Yanit hazirlaniyor...',
        voiceChatSpeaking: 'Yanit okunuyor...',
        stopListeningHint: 'Dinlemeyi durdurmak icin tiklayin',
        microphonePermission: 'Mikrofon Izni',
        microphonePermissionDescription: 'Sesli mesaj gonderebilmek icin mikrofon izni vermeniz gerekmektedir.',
        chatHistoryFetchError: 'Chat gecmisi yuklenirken hata olustu',
        chatHistoryUnavailable: 'Chat gecmisi alinamadi',
        deleteChatError: 'Sohbet silinirken hata olustu',
        noMessageFound: 'Mesaj bulunamadi',
        newChat: '+ Yeni Sohbet',
        searchChats: 'Sohbetlerde ara...',
        clearSearch: 'Aramayi temizle',
        retry: 'Tekrar Dene',
        noSearchResults: (query: string) => `"${query}" icin sonuc bulunamadi.`,
        noChatHistory: 'Henuz chat gecmisi bulunmuyor.',
        startFirstChat: 'Ilk Sohbeti Baslat',
        chatOptions: 'Sohbet secenekleri',
        chatOptionsMenu: 'Sohbet secenek menusu',
        deleteChat: 'Sil',
        deleting: 'Siliniyor...',
        deleteChatAria: 'Sohbeti sil',
        renameChat: 'Yeniden Adlandir',
        renameChatAria: 'Sohbeti yeniden adlandir',
        openSidebar: 'Yan menuyu ac',
        closeSidebar: 'Yan menuyu kapat',
        sendFile: 'Dosya Gonder',
        writeMessage: 'Mesaj yazin...',
        scrollTop: 'Yukari Cik',
        agreementRequired: 'Devam etmek icin lutfen sozlesmeyi onaylayiniz.',
        loginRequiredTitle: 'Uye olun veya giris yapin',
        loginRequiredText: 'Randevu alma islemlerine devam etmek icin uye olmaniz veya giris yapmaniz gerekmektedir. Uye olmak veya giris yapmak ister misiniz?',
        sessionExpired: 'Token refresh et',
        unknownError: 'Bilinmeyen bir hata olustu',
        selectAppointmentDate: 'Randevu Tarihi Secin',
        showingAllDates: (count: number) => `Toplam ${count} tarih gosteriliyor`,
        showingVisibleDates: (visible: number, total: number) => `${visible} / ${total} tarih gosteriliyor`,
        slotsAvailable: (count: number) => `${count} slot mevcut`,
        unavailable: 'Musait degil',
        showLess: 'Daha Az Goster',
        showMoreDates: (count: number) => `Daha Fazla Tarih (${count})`,
        back: 'Geri',
        selectTime: 'Saat Secin',
        welcome: 'Hosgeldin!',
        talkNow: 'HappAI ile simdi konus.',
        askAnything: 'Bana her konuda soru sorabilirsin.',
        startConversation: 'Konusmaya Basla'
    },
    en: {
        ok: 'OK',
        amazing: 'Great!',
        yes: 'Yes',
        no: 'No',
        info: 'Information',
        error: 'Error',
        close: 'Close',
        approve: 'Approve',
        send: 'Send',
        uploading: 'Uploading...',
        agreementTitle: 'Agreement Approval',
        agreementDescription: 'Please read and approve the agreement below. You must accept the agreement to continue.',
        serviceAgreement: 'Service Agreement',
        consentText: 'Explicit Consent Text on the Processing of Personal Data',
        uploadTitle: 'Add Your Image or File',
        uploadDescription: 'To let HappAI interpret your documents and reports, drag them into the HappAI upload area or choose a file.',
        uploadDropLabel: 'Drag and Drop or Choose File',
        uploadError: 'An error occurred while uploading the file.',
        analyzingDocuments: 'Your documents are being analyzed',
        uploadSuccess: (fileName: string) => `File uploaded successfully: ${fileName}`,
        uploadSuccessReply: 'Your file has been uploaded to HappAI. You can now ask what you want to know.',
        permissionDeniedText: 'Voice message cannot be used because microphone permission was not granted. You can try again to allow it.',
        selectCategoryFirst: 'Please select a category first.',
        speechNotSupported: 'Your browser does not support speech recognition.',
        microphoneAccessError: 'Microphone access error. Please check your browser or device speech settings.',
        stopListening: 'Stop Listening',
        voiceMessage: 'Voice Message',
        voiceChat: 'Voice Chat',
        voiceChatListening: 'Listening...',
        voiceChatThinking: 'Preparing response...',
        voiceChatSpeaking: 'Reading response...',
        stopListeningHint: 'Click to stop listening',
        microphonePermission: 'Microphone Permission',
        microphonePermissionDescription: 'You need to grant microphone permission to send voice messages.',
        chatHistoryFetchError: 'An error occurred while loading chat history',
        chatHistoryUnavailable: 'Chat history could not be retrieved',
        deleteChatError: 'An error occurred while deleting the chat',
        noMessageFound: 'No message found',
        newChat: '+ New Chat',
        searchChats: 'Search chats...',
        clearSearch: 'Clear search',
        retry: 'Try Again',
        noSearchResults: (query: string) => `No results found for "${query}".`,
        noChatHistory: 'No chat history yet.',
        startFirstChat: 'Start First Chat',
        chatOptions: 'Chat options',
        chatOptionsMenu: 'Chat options menu',
        deleteChat: 'Delete',
        deleting: 'Deleting...',
        deleteChatAria: 'Delete chat',
        renameChat: 'Rename',
        renameChatAria: 'Rename chat',
        openSidebar: 'Open sidebar',
        closeSidebar: 'Close sidebar',
        sendFile: 'Send File',
        writeMessage: 'Type a message...',
        scrollTop: 'Scroll to Top',
        agreementRequired: 'Please approve the agreement to continue.',
        loginRequiredTitle: 'Sign up or log in',
        loginRequiredText: 'You need to sign up or log in to continue booking an appointment. Would you like to sign up or log in?',
        sessionExpired: 'Refresh token',
        unknownError: 'An unknown error occurred',
        selectAppointmentDate: 'Select Appointment Date',
        showingAllDates: (count: number) => `Showing all ${count} dates`,
        showingVisibleDates: (visible: number, total: number) => `Showing ${visible} of ${total} dates`,
        slotsAvailable: (count: number) => `${count} slots available`,
        unavailable: 'Unavailable',
        showLess: 'Show Less',
        showMoreDates: (count: number) => `More Dates (${count})`,
        back: 'Back',
        selectTime: 'Select Time',
        welcome: 'Welcome!',
        talkNow: 'Talk to HappAI now.',
        askAnything: 'You can ask me anything.',
        startConversation: 'Start Chatting'
    }
} as const;

export const getTexts = () => {
    return translations[getCurrentLanguage()];
};
